/* eslint-disable import/no-cycle */
import { createAsyncThunk } from '@reduxjs/toolkit'
import { clientDetail } from 'configureBrand'
import responseCodes from 'const/responseCodes'
import { setProductType } from 'helpers/analytics'
import { userAccountInfo } from 'helpers/user'
import {
  checkEligibilityAccountNotSupported,
  registrationAccountInfo,
  selectAccountIndicator,
  selectSessionIdFromLogin
} from 'redux/loginInfo/loginInfoSlice'
import { getAuthToken } from 'redux/authToken/async-thunks'
import { AppThunkAction } from 'redux/store'
import registrationAPI, {
  SaveFirstTimeLoginResponse
} from 'services/registration'
import {
  PartialSaveUserRegistrationPayload,
  RegistrationPayload,
  SaveUserRegistrationPayload,
  UpdatePrivilegeRequest
} from 'typings/registration'
import { ServerError } from 'typings/serverError'
import { redirectTo } from 'ui/customHooks/useHistory'
import { hasAccountNotSupported } from 'helpers/brandHelpers'
import { readBlackBox } from 'helpers/windowUtility'
import { OriginalRootState } from 'redux/rootReducer'
import authTokenAPI from 'services/authToken'
import {
  selectAccessToken,
  tokenRequestSuccess
} from 'redux/authToken/authTokenSlice'
import {
  selectAccountNumber,
  selectChildAccounts
} from 'redux/demographic/demographicSlice'
import {
  completeRegistration,
  selectSessionId as selectRegistrationSessionId,
  updateRegistrationState
} from './registrationSlice'
import { UserValidateRequest } from './typings'

const { accountMismatch } = responseCodes

const { securityLevel } = clientDetail

// The final API contract is not known. Unable to clean this up completely due to several cyclical dependency issues.
export const checkRegistrationEligibility = createAsyncThunk(
  'registrationSlice/checkRegistrationEligibility',
  async (payload: RegistrationPayload, { dispatch }) => {
    const eligibilityCheckPayload = {
      ...payload,
      iovation: readBlackBox()
    }

    try {
      const response = await registrationAPI.getRegistrationEligibility(
        eligibilityCheckPayload
      )

      const {
        accountIdType,
        accountType,
        authCode,
        cardType,
        sessionId,
        'cipher.accountId': accountID
      } = response

      userAccountInfo.accountType = accountType
      userAccountInfo.accountIdType = accountIdType || ''
      userAccountInfo.accountID = accountID
      userAccountInfo.cardType = cardType
      setProductType(accountType)

      userAccountInfo.accountDetail = {
        'cipher.accountId': accountID,
        'cipher.partialAccountId': '',
        switchAccountType: 'SELF'
      }

      if (hasAccountNotSupported(accountType)) {
        redirectTo('/')
        dispatch(checkEligibilityAccountNotSupported())
        return
      }

      dispatch(getAuthToken({ authCode }))
      dispatch(
        updateRegistrationState({
          error: false,
          errorMessage: '',
          isEligibilitySuccess: true,
          sessionId,
          accountType
        })
      )
    } catch (errorResponse) {
      const { response } = errorResponse as ServerError
      const { businessErrors = [], message } = response || {}
      const { code = '' } = businessErrors[0] || {}

      let newPayload = {
        error: false,
        errorMessage: message,
        isEligibilitySuccess: false
      }
      if (code) {
        newPayload = {
          ...newPayload,
          errorMessage: code
        }
      } else {
        newPayload = {
          ...newPayload,
          error: true,
          errorMessage: message
        }
      }

      dispatch(updateRegistrationState(newPayload))
    }
  }
)

export const checkUserAvailability = createAsyncThunk(
  'registrationSlice/checkUserAvailability',
  async (payload: UserValidateRequest, { dispatch }) => {
    try {
      const response = await registrationAPI.checkUserAvailability(payload)
      const { sessionId, status } = response

      dispatch(
        updateRegistrationState({
          error: false,
          errorMessage: '',
          sessionId,
          status
        })
      )
    } catch (errorResponse) {
      const { response } = errorResponse as ServerError
      const { businessErrors = [], message } = response || {}
      const { code = '' } = businessErrors[0] || {}

      let newPayload = {
        error: true,
        errorMessage: message,
        status: ''
      }

      if (code) {
        newPayload = {
          ...newPayload,
          error: false,
          status: code
        }
      }

      dispatch(updateRegistrationState(newPayload))
    }
  }
)

/*
 * This "action" is a state update. This should be handled in a reducer. No new
 * implementations or changes should reference this "thunk". Instead, utilize
 * extra reducers adding a matcher to handle save failure.
 */
const onSaveFailure =
  (errorResponse: ServerError): AppThunkAction =>
  dispatch => {
    const { code = '', message = '' } = errorResponse.response.businessErrors[0]

    let newPayload = {
      error: true,
      errorMessage: message,
      isRegisteredSuccess: false,
      accountIndicator: '',
      isAccountError: false
    }

    if (code === accountMismatch) {
      newPayload = {
        ...newPayload,
        isAccountError: true
      }
    }

    dispatch(updateRegistrationState(newPayload))
  }

// Auth Registration Implementation
export const saveUserRegistration = createAsyncThunk(
  'registrationSlice/saveUserRegistration',
  async (payload: SaveUserRegistrationPayload, { dispatch }) => {
    try {
      const response = await registrationAPI.saveUserRegistration(payload)

      const {
        authCode,
        accountIndicator,
        accountIdType,
        'cipher.accountId': accountId,
        'cipher.partialAccountId': accountNumber
      } = response

      userAccountInfo.accountIdType = accountIdType || ''
      userAccountInfo.accountID = accountId
      userAccountInfo.securityLevel = securityLevel
      /*
       * couple of actions (completeRegistration, registrationAccountInfo) down here needs to be refactored
       * not fixing it now since their are lot's of cycle dependency
       * need to take a backlog story to refactor this
       */
      dispatch(
        completeRegistration({
          accountIndicator
        })
      )

      // token should be retrieved only for the parent account
      if (accountIndicator === 'P') {
        dispatch(
          getAuthToken({
            accountID: accountId,
            accountNumber,
            authCode,
            isRegistrationFlow: true
          })
        )
      }

      dispatch(
        registrationAccountInfo({
          accountId,
          accountNumber,
          accountIndicator
        })
      )
    } catch (errorResponse) {
      dispatch(onSaveFailure(errorResponse as ServerError))
    }
  }
)

/**
 * Payload for registrationSlice/saveFirstTimeLogin thunk
 */
export interface SaveFirstTimeLoginPayload {
  accountNumber: string
  phoneNumber: string
  email: string
  password: string
  dob: string
  firstTimeLogin: string
}

export const saveFirstTimeLogin = createAsyncThunk<
  { saveFirstTimeLoginResult: SaveFirstTimeLoginResponse },
  SaveFirstTimeLoginPayload,
  { rejectValue: unknown; state: OriginalRootState }
>(
  'registrationSlice/saveFirstTimeLogin',
  async (payload, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState()
      const accountIndicator = selectAccountIndicator(state)
      const sessionId = selectSessionIdFromLogin(state)
      const accessToken = selectAccessToken(state)

      const saveFirstTimeLoginProps = {
        ...payload,
        sessionId,
        accessToken,
        iovation: readBlackBox()
      }
      const { data } = await registrationAPI.saveFirstTimeLogin(
        saveFirstTimeLoginProps
      )

      if (accountIndicator === 'P' && data) {
        const { authCode } = data
        const tokenInfo = await authTokenAPI.getAuthToken({
          authCode
        })
        dispatch(tokenRequestSuccess(tokenInfo))
      }

      return { saveFirstTimeLoginResult: data }
    } catch (errorResponse) {
      return rejectWithValue(errorResponse)
    }
  }
)

// Implementation for partial registration (applicable to without auth code registration)
export const partialSaveUserRegistration = createAsyncThunk(
  'registrationSlice/partialSaveUserRegistration',
  async (payload: PartialSaveUserRegistrationPayload, { dispatch }) => {
    try {
      const userRegistrationPayload = {
        ...payload,
        iovation: readBlackBox()
      }
      await registrationAPI.partialSaveUserRegistration(userRegistrationPayload)

      dispatch(
        updateRegistrationState({
          error: false,
          errorMessage: '',
          isRegisteredSuccess: true,
          accountIndicator: ''
        })
      )
    } catch (errorResponse) {
      dispatch(onSaveFailure(errorResponse as ServerError))
    }
  }
)

type UpdateChildPrivilegePayload = Pick<
  UpdatePrivilegeRequest,
  'userPrivilegeInfo'
>

export const updateChildPrivilege = createAsyncThunk<
  unknown,
  UpdateChildPrivilegePayload,
  { state: OriginalRootState }
>(
  'registrationSlice/updateChildPrivilege',
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState()
      const accessToken = selectAccessToken(state)
      const accountNumber = selectAccountNumber(state)
      const accountList = selectChildAccounts(state).map(
        account => account.accountUniqueIdentifier
      )

      /*
       * get session id from login for First Time Login flow and fallback to
       * registration slice for registration flow
       */
      const sessionId =
        selectSessionIdFromLogin(state) || selectRegistrationSessionId(state)

      return await registrationAPI.updateChildAccountPrivilege({
        ...payload,
        accountList,
        sessionId,
        accessToken,
        accountNumber
      })
    } catch (error) {
      return rejectWithValue(error as unknown)
    }
  }
)
